/**
 * 🟥 CUADRO NEGRO Tabla Free vs Premium (feb-2026) — versión responsive.
 *
 * Reglas de breakpoints (cuadro negro §1):
 *   - Mobile (≤480px aprox / clases base sin prefix): tarjetas verticales,
 *     cada beneficio en una card propia. NO scroll horizontal.
 *   - Tablet (≥768px / md): dos columnas — Beneficio + Free/Premium apilados.
 *   - Desktop (≥1024px / lg): tabla completa de 3 columnas.
 *
 * Filas, columnas y etiquetas son EXACTAS al cuadro negro original — no
 * se permiten sinónimos, reescrituras ni reordenamientos.
 */
import React from 'react';
import { Check, Minus } from 'lucide-react';

// Filas oficiales — orden EXACTO.
const ROWS = [
  { benefit: 'Chat con Lyon IA',                            free: 'Ilimitado',           premium: 'Ilimitado' },
  { benefit: 'Acciones avanzadas del Builder',              free: 'No incluido',         premium: 'Incluido'  },
  { benefit: 'Style Packs y recetas exclusivas',            free: 'No incluido',         premium: 'Incluido'  },
  { benefit: 'Inferencia de estilos y análisis inteligente', free: 'No incluido',        premium: 'Incluido'  },
  { benefit: 'Acceso prioritario a nuevas funciones',       free: 'No incluido',         premium: 'Incluido'  },
  { benefit: 'Imágenes y mensajes',                         free: 'Limitados por semana', premium: 'Ilimitados' },
];

const renderCell = (text) => {
  if (text === 'Incluido') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-300">
        <Check className="w-4 h-4" />
        Incluido
      </span>
    );
  }
  if (text === 'No incluido') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-zinc-500">
        <Minus className="w-4 h-4" />
        No incluido
      </span>
    );
  }
  if (text === 'Limitado' || text === 'Limitados por semana') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-yellow-500/15 border border-yellow-500/30 text-yellow-200">
        {text}
      </span>
    );
  }
  if (text === 'Ilimitado' || text === 'Ilimitados') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gradient-to-r from-amber-400/25 to-fuchsia-400/20 border border-amber-400/40 text-amber-200">
        {text}
      </span>
    );
  }
  return <span className="text-sm text-zinc-300">{text}</span>;
};

const SUMMARY_TEXT = 'Total mensual: $9 USD · Total anual: $86 USD (ahorrá $22)';

export const FreeVsPremiumTable = () => {
  return (
    <section
      className="my-6"
      data-testid="free-vs-premium-table-section"
      aria-label="Comparativa Free vs Premium"
    >
      {/* ────────────────────────────────────────────────────────────────
          MOBILE / TABLET (<lg) — tarjetas verticales
          §5.1-5.2: una card por beneficio, fondo #111, borde #333,
          padding 16px, sin scroll horizontal.
       ──────────────────────────────────────────────────────────────── */}
      <div
        className="lg:hidden space-y-3"
        data-testid="free-vs-premium-cards-mobile"
      >
        {ROWS.map((row, i) => (
          <div
            key={row.benefit}
            data-testid="mobile-benefit-card"
            className="rounded-2xl bg-[#111] border border-[#333] p-4 shadow-md shadow-black/30 break-words"
          >
            <h4 className="text-sm font-semibold text-white mb-3">
              {row.benefit}
            </h4>
            {/* En tablet ≥md mostramos Free y Premium en 2 columnas
                apiladas en grilla horizontal; en mobile uno arriba/otro
                abajo. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex items-center justify-between md:justify-start md:gap-3 p-2 rounded-lg bg-zinc-900/60 border border-zinc-800/70">
                <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold shrink-0">
                  Free
                </span>
                <div className="text-right md:text-left">{renderCell(row.free)}</div>
              </div>
              <div className="flex items-center justify-between md:justify-start md:gap-3 p-2 rounded-lg bg-amber-500/[0.04] border border-amber-500/20">
                <span className="text-[11px] uppercase tracking-wider text-amber-300/80 font-semibold shrink-0">
                  Premium
                </span>
                <div className="text-right md:text-left">{renderCell(row.premium)}</div>
              </div>
            </div>
          </div>
        ))}

        {/* Summary row (mobile/tablet) */}
        <div
          className="rounded-2xl bg-gradient-to-r from-amber-500/15 via-fuchsia-500/10 to-amber-500/15 border border-amber-500/40 px-4 py-3 text-center text-sm font-semibold text-amber-100"
          data-testid="free-vs-premium-summary-row"
        >
          {SUMMARY_TEXT}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────────
          DESKTOP (lg+) — tabla completa de 3 columnas
       ──────────────────────────────────────────────────────────────── */}
      <div className="hidden lg:block rounded-2xl border border-zinc-700/70 bg-zinc-900/60 overflow-hidden shadow-lg shadow-black/20">
        {/* Header */}
        <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-zinc-900 border-b border-zinc-800">
          <div className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-400 font-semibold">
            Beneficio
          </div>
          <div className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-400 font-semibold border-l border-zinc-800">
            Plan Free
          </div>
          <div className="px-4 py-3 text-xs uppercase tracking-wider text-amber-300 font-semibold border-l border-zinc-800">
            Plan Premium
          </div>
        </div>

        {/* Rows */}
        <div data-testid="desktop-benefit-table">
          {ROWS.map((row, i) => (
            <div
              key={row.benefit}
              data-testid={`free-vs-premium-row-${i}`}
              className={`grid grid-cols-[1.4fr_1fr_1fr] items-center border-t border-zinc-800/70 ${
                i % 2 === 0 ? 'bg-zinc-900/40' : 'bg-zinc-900/10'
              }`}
            >
              <div className="px-4 py-3 text-sm text-zinc-200">{row.benefit}</div>
              <div className="px-4 py-3 border-l border-zinc-800">{renderCell(row.free)}</div>
              <div className="px-4 py-3 border-l border-zinc-800">{renderCell(row.premium)}</div>
            </div>
          ))}
        </div>

        {/* Summary row (desktop) */}
        <div
          className="border-t border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-fuchsia-500/5 to-amber-500/10 px-4 py-3 text-center text-sm font-semibold text-amber-100"
        >
          {SUMMARY_TEXT}
        </div>
      </div>
    </section>
  );
};

export default FreeVsPremiumTable;
